import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Spinner } from "Components/ui";
import { CircleCheck, Clock, RefreshCw, TriangleAlert } from "Components/ui/icons";
import { cn } from "utils/cn";
import { formatINR } from "utils/format";
import { GatewayReturn, ReceiptStatus } from "api/billingPublic";
import {
  describePublicError,
  readGatewayReturn,
  usePayReturnData,
  useStatementReturnData,
} from "./data";
import PayShell from "./Components/PayShell";
import InvoiceSummary from "./Components/InvoiceSummary";

type StatusTone = "success" | "warning" | "neutral" | "progress";

const TONE_CLASSES: Record<StatusTone, string> = {
  success: "bg-success-subtle text-success-text",
  warning: "bg-warning-subtle text-warning-text",
  neutral: "bg-surface-muted text-muted-foreground",
  progress: "bg-primary-subtle text-primary-text",
};

const StatusBlock: React.FC<{
  icon: React.ReactNode;
  tone: StatusTone;
  title: string;
  children?: React.ReactNode;
}> = ({ icon, tone, title, children }) => (
  <div className="py-4 text-center">
    <span
      className={cn(
        "mx-auto flex h-14 w-14 items-center justify-center rounded-full [&_svg]:h-7 [&_svg]:w-7",
        TONE_CLASSES[tone]
      )}
    >
      {icon}
    </span>
    <p className="mt-4 text-lg font-semibold text-foreground">{title}</p>
    {children && <div className="mt-1 text-sm text-muted-foreground">{children}</div>}
  </div>
);

/** Back from a statement link (/pay/return?statement=…): everything that was due. */
/** What the payer can expect about the receipt; never promises an email that won't come. */
const ReceiptLine: React.FC<{ number: string | null; status: ReceiptStatus | null }> = ({
  number,
  status,
}) => {
  if (!number) {
    return null;
  }
  const receipt = <span className="font-mono text-foreground">{number}</span>;
  return (
    <p className="mt-2">
      {status === "sent" ? (
        <>Receipt {receipt} has been sent to you.</>
      ) : status === "pending" ? (
        <>Receipt {receipt} will be sent to you shortly.</>
      ) : (
        <>Your receipt number is {receipt}. Keep it for your records.</>
      )}
    </p>
  );
};

const StatementReturn: React.FC<{ token: string; gatewayReturn: GatewayReturn | null }> = ({
  token,
  gatewayReturn,
}) => {
  const { outcome, statement, query, checkAgain, receiptNumber, receiptStatus } =
    useStatementReturnData(token, gatewayReturn);
  const payAgainPath = `/pay/s/${encodeURIComponent(token)}`;

  let content: React.ReactNode;
  switch (outcome) {
    case "missing_token":
    case "invalid":
      content = (
        <StatusBlock
          icon={<TriangleAlert aria-hidden="true" />}
          tone="warning"
          title="We couldn't find this payment"
        >
          Please open the payment link from TeleOpd again.
        </StatusBlock>
      );
      break;
    case "loading":
      content = <StatusBlock icon={<Spinner />} tone="progress" title="Checking your payment…" />;
      break;
    case "error":
      content = (
        <StatusBlock
          icon={<TriangleAlert aria-hidden="true" />}
          tone="warning"
          title="We couldn't check the payment right now"
        >
          <p>{describePublicError(query.error)}</p>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw />}
            onClick={checkAgain}
            className="mt-4"
          >
            Try again
          </Button>
        </StatusBlock>
      );
      break;
    case "paid":
      content = (
        <StatusBlock
          icon={<CircleCheck aria-hidden="true" />}
          tone="success"
          title="Payment received"
        >
          <p>
            Thank you! Everything due is paid. TeleOpd goes back to normal within a minute; you can
            close this page.
          </p>
          <ReceiptLine number={receiptNumber} status={receiptStatus} />
        </StatusBlock>
      );
      break;
    case "partial":
      content = (
        <StatusBlock
          icon={<CircleCheck aria-hidden="true" />}
          tone="success"
          title="Payment received"
        >
          <p>
            Thank you! {statement ? formatINR(statement.amountDue) : "Some amount"} is still due.
          </p>
          <Button asChild className="mt-4">
            <Link to={payAgainPath}>Pay the rest</Link>
          </Button>
        </StatusBlock>
      );
      break;
    case "confirming":
      content = (
        <StatusBlock icon={<Spinner />} tone="progress" title="Confirming your payment…">
          This usually takes a few seconds. Please keep this page open.
        </StatusBlock>
      );
      break;
    default:
      content = (
        <StatusBlock
          icon={<Clock aria-hidden="true" />}
          tone="warning"
          title="Still waiting for confirmation"
        >
          <p>
            We haven't received confirmation from the payment provider yet. If money left your
            account, it will be applied automatically within a few minutes, so there's no need to
            pay again.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button leftIcon={<RefreshCw />} onClick={checkAgain}>
              Check again
            </Button>
            <Button asChild variant="secondary">
              <Link to={payAgainPath}>Back to what's due</Link>
            </Button>
          </div>
        </StatusBlock>
      );
  }
  return <PayShell payee={statement ? statement.payee : undefined}>{content}</PayShell>;
};

/** Public /pay/return?token=… (one invoice) or ?statement=… page the gateway sends payers to. */
const PayReturn: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const statementToken = (params.get("statement") || "").trim();
  const gatewayReturn = React.useMemo(
    () => readGatewayReturn(new URLSearchParams(location.search)),
    [location.search]
  );
  return statementToken ? (
    <StatementReturn token={statementToken} gatewayReturn={gatewayReturn} />
  ) : (
    <InvoiceReturn gatewayReturn={gatewayReturn} />
  );
};

/** Back from one invoice's link (/pay/return?token=…). */
const InvoiceReturn: React.FC<{ gatewayReturn: GatewayReturn | null }> = ({ gatewayReturn }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = (params.get("token") || "").trim();
  const gatewaySaysPaid = params.get("razorpay_payment_link_status") === "paid";
  const { outcome, invoice, query, checkAgain, receiptNumber, receiptStatus } = usePayReturnData(
    token,
    gatewayReturn
  );
  const payAgainPath = token ? `/pay/${encodeURIComponent(token)}` : null;

  let content: React.ReactNode;
  switch (outcome) {
    case "missing_token":
    case "invalid":
      content = (
        <StatusBlock
          icon={<TriangleAlert aria-hidden="true" />}
          tone="warning"
          title="We couldn't find this payment"
        >
          This link is missing its invoice reference or has expired. Please open the pay link from
          your invoice email again.
        </StatusBlock>
      );
      break;
    case "loading":
      content = <StatusBlock icon={<Spinner />} tone="progress" title="Checking your payment…" />;
      break;
    case "error":
      content = (
        <StatusBlock
          icon={<TriangleAlert aria-hidden="true" />}
          tone="warning"
          title="We couldn't check the payment right now"
        >
          <p>{describePublicError(query.error)}</p>
          <Button
            variant="secondary"
            leftIcon={<RefreshCw />}
            onClick={checkAgain}
            className="mt-4"
          >
            Try again
          </Button>
        </StatusBlock>
      );
      break;
    case "paid":
      content = (
        <StatusBlock
          icon={<CircleCheck aria-hidden="true" />}
          tone="success"
          title="Payment received"
        >
          <p>Thank you! Your TeleOpd will be fully active within a minute.</p>
          <ReceiptLine number={receiptNumber} status={receiptStatus} />
        </StatusBlock>
      );
      break;
    case "partial":
      content = (
        <StatusBlock
          icon={<CircleCheck aria-hidden="true" />}
          tone="success"
          title="Payment received"
        >
          <p>
            Thank you! {invoice ? formatINR(invoice.amountDue) : "Some amount"} is still due on this
            invoice.
          </p>
          {payAgainPath && (
            <Button asChild className="mt-4">
              <Link to={payAgainPath}>Pay the remaining amount</Link>
            </Button>
          )}
        </StatusBlock>
      );
      break;
    case "void":
      content = (
        <StatusBlock
          icon={<TriangleAlert aria-hidden="true" />}
          tone="neutral"
          title="This invoice was cancelled"
        >
          If you were charged, please contact Meta16 Labs and we'll sort it out.
        </StatusBlock>
      );
      break;
    case "confirming":
      content = (
        <StatusBlock
          icon={<Spinner />}
          tone="progress"
          title={
            gatewaySaysPaid ? "Payment successful, applying it…" : "We're confirming your payment…"
          }
        >
          This usually takes a few seconds. Please keep this page open.
        </StatusBlock>
      );
      break;
    default:
      content = (
        <StatusBlock
          icon={<Clock aria-hidden="true" />}
          tone="warning"
          title="Still waiting for confirmation"
        >
          <p>
            We haven't received confirmation from the payment provider yet. If money left your
            account, it will be applied automatically within a few minutes, so there's no need to
            pay again.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button leftIcon={<RefreshCw />} onClick={checkAgain}>
              Check again
            </Button>
            {payAgainPath && (
              <Button asChild variant="secondary">
                <Link to={payAgainPath}>Back to the invoice</Link>
              </Button>
            )}
          </div>
        </StatusBlock>
      );
  }

  return (
    <PayShell payee={invoice ? invoice.payee : undefined}>
      {content}
      {invoice && outcome !== "invalid" && (
        <div className="mt-6 border-t border-border pt-5">
          <InvoiceSummary invoice={invoice} compact />
        </div>
      )}
    </PayShell>
  );
};

export default PayReturn;
